import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";
import { TYPE_BADGE_COLORS } from "../../utils/typeColors";

interface Props { payload: VisualPayload }

function MoveList({ label, moves }: { label: string; moves: Array<{ name: string; type?: string; power?: number; energy_delta?: number; cooldown_ms?: number; is_legacy?: boolean; is_elite?: boolean }> }) {
  if (!moves.length) return null;
  return (
    <div>
      <p className="text-[11px] text-white/30 uppercase tracking-wide mb-2">{label}</p>
      <ul className="space-y-1.5">
        {moves.map((m) => {
          const badgeClass = m.type ? (TYPE_BADGE_COLORS[m.type.toLowerCase()] ?? "bg-white/10 text-white/50") : "";
          const turns = m.cooldown_ms ? m.cooldown_ms / 500 : null;
          const dpt = (m.power != null && turns) ? (m.power / turns).toFixed(2) : null;
          const ept = (m.energy_delta != null && m.energy_delta > 0 && turns) ? (m.energy_delta / turns).toFixed(2) : null;
          const dpe = (m.power != null && m.energy_delta != null && m.energy_delta < 0) ? (m.power / Math.abs(m.energy_delta)).toFixed(2) : null;
          return (
            <li key={m.name} className="flex flex-col py-1 px-2 rounded-lg hover:bg-white/[0.03]">
              <div className="flex items-center gap-2">
                <span className="flex-1 text-sm text-white/80">{m.name}</span>
                {m.type && (
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${badgeClass}`}>{m.type}</span>
                )}
                {m.is_elite && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400 font-medium">Elite</span>
                )}
                {m.is_legacy && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 font-medium">Legacy</span>
                )}
              </div>
              {(dpt || ept || dpe) && (
                <p className="text-[11px] text-white/30 mt-0.5">
                  {m.power != null && <>{m.power} PWR · </>}
                  {m.energy_delta != null && <>{m.energy_delta > 0 ? `+${m.energy_delta}` : m.energy_delta} E · </>}
                  {turns != null && <>{turns}T · </>}
                  {dpt && <>{dpt} DPT</>}
                  {ept && <> · {ept} EPT</>}
                  {dpe && <> · {dpe} DPE</>}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function MovesetCard({ payload }: Props) {
  const fastSec   = payload.sections.find((s) => s.section === "fast_moves");
  const chargeSec = payload.sections.find((s) => s.section === "charge_moves");
  const hasMoves  = (fastSec && (fastSec.moves as unknown[]).length > 0) || (chargeSec && (chargeSec.moves as unknown[]).length > 0);

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      {hasMoves ? (
        <div className="space-y-4">
          {fastSec && <MoveList label="Fast Moves" moves={fastSec.moves as Array<{ name: string; type?: string; power?: number; energy_delta?: number; cooldown_ms?: number; is_legacy?: boolean; is_elite?: boolean }>} />}
          {chargeSec && <MoveList label="Charged Moves" moves={chargeSec.moves as Array<{ name: string; type?: string; power?: number; energy_delta?: number; cooldown_ms?: number; is_legacy?: boolean; is_elite?: boolean }>} />}
        </div>
      ) : (
        <p className="text-sm text-white/40">No move data.</p>
      )}
    </CardShell>
  );
}
